import { format } from 'date-fns';
import type { ApexOptions } from 'apexcharts';
import { DATE_FORMAT } from '@/lib/constants';
import mergeWith from 'lodash/mergeWith';
import { parseDateFromString } from '@/lib/helpers';

/**
 * Chart display type constants for visualization
 */
export const CHART_DISPLAY_TYPES = {
    BAR: 'bar',
    AREA: 'area',
} as const;

// Chart constants
const DEFAULT_FONT_FAMILY = 'Inter, ui-sans-serif, system-ui';
const DEFAULT_FORE_COLOR = 'var(--text-color-light)';
const BAR_YAXIS_COLOR = 'var(--bg-color-neutral-inverse-strong)';
const LINE_YAXIS_COLOR = 'var(--bg-color-neutral-inverse-strong)';
const AREA_YAXIS_COLOR = 'var(--bg-color-neutral-inverse-strong)';
const XAXIS_LABEL_COLOR = 'var(--text-color-light)';
const DEFAULT_BORDER_RADIUS = 5;
const LINE_TOOLTIP_FONT_SIZE = '12px';

const RESPONSIVE_BREAKPOINTS = [
    { breakpoint: 350, options: { xaxis: { tickAmount: 2 } } },
    { breakpoint: 600, options: { xaxis: { tickAmount: 4 } } },
    { breakpoint: 960, options: { xaxis: { tickAmount: 6 } } },
    { breakpoint: 1600, options: { xaxis: { tickAmount: 8 } } },
] as const;

/**
 * Shared chart utilities for ApexCharts-based components.
 * Used by StanLineChart, StanBarChart, and other chart components.
 */

// Keeps a numeric input within provided lower and upper bounds.
export function clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value));
}

// Shortens large numbers with K/M suffixes while preserving decimals on small values.
export function abbreviateNumber(num: number, decimals = 2) {
    const value = Number.isFinite(num) ? num : 0;
    if (Math.abs(value) < 1e3) return value.toFixed(decimals);
    if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(0)}M`;
    return `${(value / 1e3).toFixed(0)}K`;
}

// Appends an alpha channel to a hex color string using the provided opacity.
export function hexWithAlpha(hex: string, alpha: number) {
    const raw = hex.replace('#', '');
    const a = clamp(Math.round(alpha * 255), 0, 255)
        .toString(16)
        .padStart(2, '0');
    return `#${raw}${a}`;
}

// Generates a translucent palette by applying preset alpha steps to a base color.
export function buildStackedPalette(base: string) {
    return [1, 0.85, 0.6, 0.45, 0.3].map(alpha => hexWithAlpha(base, alpha));
}

// Produces a human-friendly label for chart axes while handling invalid or empty inputs.
export function formatDateLabel(value: string) {
    const date = parseDateFromString(value);
    if (!date) {
        // Handle both cases: plain string and date strings with periods
        return String(value ?? '').replace('.', ' ');
    }
    return format(date, DATE_FORMAT.CHART_DATE);
}

// Deep merges ApexCharts option objects while letting overrides replace arrays wholesale.
export function mergeOptions(base: ApexOptions, overrides?: ApexOptions) {
    if (!overrides) return base;
    return mergeWith({}, base, overrides, (objValue, srcValue) =>
        Array.isArray(objValue) && Array.isArray(srcValue) ? srcValue : undefined
    );
}

// Type definition for ApexCharts tooltip context
export interface TooltipContext {
    series: number[][];
    seriesIndex: number;
    dataPointIndex: number;
    w: {
        globals: {
            seriesNames: string[];
        };
    };
}

/**
 * Creates a branded tooltip with Stan logo.
 * @param formatValue Function to format the tooltip value
 * @param logoUrl URL to the logo image
 * @param tooltipClassName CSS class name for the tooltip container
 */
export function buildBrandedTooltip(
    formatValue: (v: number | null) => string,
    tooltipClassName: string
): ApexOptions['tooltip'] {
    return {
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }: TooltipContext) => {
            const value = series?.[seriesIndex]?.[dataPointIndex] ?? null;
            const name = w?.globals?.seriesNames?.[seriesIndex] ?? '';
            return `
                <div class="${tooltipClassName}">
                    <svg width="24" height="24" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="1024" height="1024" rx="500" fill="url(#paint0_linear_5748_9590)"/>
                        <path d="M329.875 438.611C324.791 398.461 336.143 358.789 361.821 326.93C388.572 293.719 426.886 274.29 466.988 269.792V234.846C466.988 216.661 482.19 201.922 500.946 201.922C519.701 201.922 534.903 216.661 534.903 234.846V276.671C551.166 281.983 566.788 289.998 581.064 300.814L639.268 344.9C654.051 356.098 656.667 376.771 645.117 391.103C633.568 405.436 612.233 407.972 597.463 396.786L539.259 352.7C500.896 323.652 445.308 330.268 415.336 367.451C400.813 385.468 394.409 407.888 397.284 430.583C400.159 453.279 411.968 473.533 430.55 487.602L513.495 550.435C528.277 561.633 530.893 582.319 519.356 596.651C512.656 604.966 502.673 609.297 492.58 609.297C485.275 609.297 477.908 607.024 471.702 602.322L388.758 539.489C355.861 514.58 334.959 478.761 329.875 438.611Z" fill="white"/>
                        <path d="M626.261 493.156L545.656 430.328C531.293 419.131 510.563 421.667 499.353 435.998C488.13 450.33 490.672 471.013 505.036 482.21L585.629 545.038C603.685 559.106 615.171 579.359 617.964 602.052C620.758 624.745 614.523 647.163 600.424 665.179C583.519 686.76 558.257 698.053 532.731 698.041C514.267 698.041 495.66 692.131 480.014 679.941L423.459 635.859C409.096 624.662 388.366 627.198 377.144 641.529C365.922 655.86 368.475 676.544 382.839 687.741L439.381 731.823C453.241 742.637 468.432 750.652 484.234 755.964V797.797C484.234 815.981 499.005 830.719 517.229 830.719C535.453 830.719 550.224 815.981 550.224 797.797V762.854C589.202 758.368 626.417 738.941 652.422 705.733C703.941 639.938 692.215 544.583 626.273 493.18L626.261 493.156Z" fill="white"/>
                        <defs>
                        <linearGradient id="paint0_linear_5748_9590" x1="303.407" y1="1.30018e-05" x2="743.667" y2="985.367" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#6D55FF"/>
                        <stop offset="1" stop-color="#3E68FF"/>
                        </linearGradient>
                        </defs>
                    </svg>
                    <span>${name}:</span>
                    <b>&nbsp;${formatValue(value)}</b>
                </div>
            `;
        },
    };
}

/**
 * Creates a plain tooltip without branding.
 * @param formatValue Function to format the tooltip value
 * @param options Optional configuration for the tooltip
 */
export function buildPlainTooltip(
    formatValue: (v: number | null) => string,
    options: { includeMarker?: boolean } = {}
): ApexOptions['tooltip'] {
    const { includeMarker = false } = options;
    return {
        enabled: true,
        y: { formatter: (val: number) => formatValue(val) },
        ...(includeMarker ? { marker: { show: true } } : {}),
    };
}

/**
 * Builds a color palette based on priority: series colors > custom color > defaults.
 * Supports stacked palettes with alpha gradients.
 * @param seriesColors Array of colors from series data
 * @param customColor Custom color provided by user
 * @param defaultColors Default color array
 * @param defaultColor Fallback single color for stacked palettes
 * @param stacked Whether to generate stacked palette
 */
export function buildChartPalette(
    seriesColors: string[],
    customColor: string | undefined,
    defaultColors: string[],
    defaultColor: string,
    stacked: boolean
): string[] {
    if (seriesColors.length) {
        return seriesColors;
    }
    if (customColor) {
        return stacked ? buildStackedPalette(customColor) : [customColor];
    }
    return stacked ? buildStackedPalette(defaultColor) : defaultColors;
}

/**
 * Creates a value formatter function for chart tooltips and labels.
 * @param tooltipFormatter Custom formatter if provided
 * @param chartPrefix Prefix string (e.g., '$')
 * @param dataSuffix Suffix string (e.g., '%')
 * @param decimals Number of decimal places
 * @param useAbbreviation Whether to abbreviate large numbers (1K, 1M)
 */
export function buildValueFormatter(
    tooltipFormatter: ((val: number | null) => string) | undefined,
    chartPrefix: string,
    dataSuffix: string,
    decimals: number,
    useAbbreviation: boolean
): (val: number | null) => string {
    if (tooltipFormatter) return tooltipFormatter;

    if (useAbbreviation) {
        return (val: number | null) => `${chartPrefix}${abbreviateNumber(val ?? 0, decimals)}${dataSuffix}`;
    }

    return (val: number | null) => `${chartPrefix}${(val ?? 0).toFixed(decimals)}${dataSuffix}`;
}

/**
 * Creates common base chart configuration shared across all chart types.
 * @param chartType ApexCharts type (bar, line, area, etc.)
 * @param palette Color array
 * @param fontFamily Font family string
 * @param foreColor Foreground color
 */
export function buildBaseChartConfig(
    chartType: 'bar' | 'line' | 'area' | undefined,
    palette: string[],
    fontFamily: string,
    foreColor: string
) {
    return {
        colors: palette,
        chart: {
            type: chartType,
            fontFamily,
            foreColor,
            toolbar: { show: false },
        },
        dataLabels: { enabled: false },
    };
}

/**
 * Creates common hover/active state configuration (disables filters).
 */
export function buildChartStates(): ApexOptions['states'] {
    return {
        hover: {
            filter: {
                type: 'none',
            },
        },
        active: {
            filter: {
                type: 'none',
            },
        },
    };
}

/**
 * Creates common xaxis configuration structure.
 * @param labelColor Color for axis labels
 */
export function buildBaseXAxisConfig(labelColor: string) {
    return {
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
        labels: {
            hideOverlappingLabels: true,
            style: { fontSize: '10px', colors: labelColor },
        },
    };
}

/**
 * Creates common yaxis configuration structure.
 * @param formatValue Value formatter function
 * @param yAxisColor Color for y-axis labels
 */
export function buildBaseYAxisConfig(formatValue: (val: number) => string, yAxisColor: string) {
    return {
        opposite: true,
        tickAmount: 2,
        showForNullSeries: false,
        labels: {
            formatter: formatValue,
            style: { colors: yAxisColor },
        },
    };
}

/**
 * Combines entries beyond topN into an "Other" category.
 * Useful for limiting the number of items displayed in charts.
 * @param totals Object mapping labels to values
 * @param topN Number of top items to keep separate
 * @param otherLabel Label for the combined "other" category
 */
export function combineIntoOther(
    totals: Record<string, number>,
    topN: number,
    otherLabel: string
): Record<string, number> {
    const entries = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    const top = entries.slice(0, topN);
    const rest = entries.slice(topN);
    const othersTotal = rest.reduce((acc, [, v]) => acc + v, 0);
    const next: Record<string, number> = Object.fromEntries(top);
    if (othersTotal > 0) next[otherLabel] = othersTotal;
    return next;
}

// Type definition for resolved config used by variant builders
export interface ResolvedConfig {
    chartType: 'bar' | 'line' | 'area';
    palette: string[];
    formatValue: (val: number | null) => string;
}

/**
 * Bar chart variant options builder
 */
export function variantOptionsBarBuilder(
    resolved: ResolvedConfig,
    options: {
        stacked: boolean;
        categories?: (string | number)[];
        yAxisMin?: number;
        yAxisMax?: number;
        borderRadius?: number;
    }
): ApexOptions {
    const { stacked, categories, yAxisMin, yAxisMax, borderRadius } = options;
    const resolvedBorderRadius = borderRadius ?? (stacked ? 0 : DEFAULT_BORDER_RADIUS);

    const baseConfig = buildBaseChartConfig(
        resolved.chartType as 'bar' | 'line' | 'area',
        resolved.palette,
        DEFAULT_FONT_FAMILY,
        DEFAULT_FORE_COLOR
    );

    const baseXAxis = buildBaseXAxisConfig(XAXIS_LABEL_COLOR);
    const baseYAxis = buildBaseYAxisConfig((val: number) => resolved.formatValue(val), BAR_YAXIS_COLOR);

    return {
        ...baseConfig,
        chart: {
            ...baseConfig.chart,
            stacked,
            animations: {
                enabled: true,
                speed: 200,
                animateGradually: {
                    enabled: true,
                    delay: 50,
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350,
                },
            },
            zoom: { enabled: false },
        },
        stroke: {
            show: true,
            width: 2,
            colors: resolved.palette,
        },
        fill: {
            opacity: 1,
            type: 'solid',
            colors: resolved.palette,
        },
        grid: { show: false },
        legend: { show: false },
        plotOptions: {
            bar: {
                borderRadius: resolvedBorderRadius,
                columnWidth: '80%',
            },
        },
        xaxis: {
            ...baseXAxis,
            type: categories ? 'category' : 'datetime',
            categories,
            tickAmount: 8,
            tickPlacement: 'on',
            crosshairs: { show: false },
            labels: {
                ...baseXAxis.labels,
                offsetX: 2,
                rotate: 0,
                formatter: formatDateLabel,
            },
        },
        yaxis: {
            ...baseYAxis,
            max: yAxisMax,
            min: yAxisMin,
            labels: {
                ...baseYAxis.labels,
                show: false,
            },
        },
        responsive: [...RESPONSIVE_BREAKPOINTS],
    };
}

/**
 * Line chart variant options builder
 */
export function variantOptionsLineBuilder(
    resolved: ResolvedConfig,
    options: {
        stacked: boolean;
        categories?: (string | number)[];
        yAxisMin?: number;
        yAxisMax?: number;
        enableZoom?: boolean;
        stroke?: ApexOptions['stroke'];
        markers?: ApexOptions['markers'];
    }
): ApexOptions {
    const { categories, yAxisMin, yAxisMax, enableZoom, stroke, markers } = options;

    const baseConfig = buildBaseChartConfig(
        resolved.chartType as 'line' | 'area',
        resolved.palette,
        DEFAULT_FONT_FAMILY,
        DEFAULT_FORE_COLOR
    );
    const baseStates = buildChartStates();
    const baseXAxis = buildBaseXAxisConfig(XAXIS_LABEL_COLOR);
    const baseYAxis = buildBaseYAxisConfig((val: number) => resolved.formatValue(val), LINE_YAXIS_COLOR);

    const markerOptions: ApexOptions['markers'] = markers ?? {
        size: 1,
        strokeWidth: 0,
    };
    const strokeOptions: ApexOptions['stroke'] = stroke ?? {
        show: true,
        width: 2,
        colors: resolved.palette,
    };

    return {
        ...baseConfig,
        chart: {
            ...baseConfig.chart,
            animations: { enabled: true },
            zoom: enableZoom
                ? {
                      enabled: true,
                      type: 'x',
                      autoScaleYaxis: true,
                      allowMouseWheelZoom: true,
                  }
                : { enabled: false },
        },
        stroke: strokeOptions,
        markers: markerOptions,
        grid: { strokeDashArray: 0 },
        legend: {
            show: true,
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: LINE_TOOLTIP_FONT_SIZE,
        },
        states: baseStates,
        xaxis: {
            ...baseXAxis,
            type: categories ? 'category' : 'datetime',
            categories,
            axisTicks: { show: true },
            tickPlacement: 'on',
            labels: {
                ...baseXAxis.labels,
                rotate: -45,
                trim: true,
                formatter: (value: string) => (categories ? String(value ?? '') : formatDateLabel(value)),
            },
            tooltip: { enabled: false },
            group: { style: { fontSize: '10px', fontWeight: 700 } },
        },
        yaxis: {
            ...baseYAxis,
            opposite: false,
            max: yAxisMax,
            min: yAxisMin,
            labels: {
                ...baseYAxis.labels,
                show: true,
                minWidth: 50,
            },
        },
        responsive: [...RESPONSIVE_BREAKPOINTS],
    };
}

/**
 * Resolves a CSS variable to its computed hex value.
 * Required for ApexCharts gradient fills which don't support CSS variables directly.
 * @param cssVar CSS variable name (e.g., 'var(--color-brand-main)')
 * @returns Hex color value or fallback hex if resolution fails
 */
function resolveCssVariableToHex(cssVar: string): string {
    // Extract the CSS variable name from 'var(--variable-name)'
    const match = cssVar.match(/var\((--[^)]+)\)/);
    if (!match || !match[1]) return cssVar; // Return as-is if not a CSS variable

    const varName = match[1];
    // Only resolve in browser environment
    if (typeof window === 'undefined') {
        // SSR fallback: return stan-500 hex value
        return '#6355ff';
    }

    try {
        const computedStyle = getComputedStyle(document.documentElement);
        const value = computedStyle.getPropertyValue(varName)?.trim();
        if (value) {
            return value;
        }
    } catch (error) {
        console.warn(`Failed to resolve CSS variable ${varName}:`, error);
    }

    // Fallback to stan-500 hex if resolution fails
    return '#6355ff';
}

/**
 * Area chart variant options builder
 */
export function variantOptionsAreaBuilder(
    resolved: ResolvedConfig,
    options: {
        stacked: boolean;
        categories?: (string | number)[];
        yAxisMin?: number;
        yAxisMax?: number;
        stroke?: ApexOptions['stroke'];
    }
): ApexOptions {
    const { stacked, categories, yAxisMin, yAxisMax, stroke } = options;

    const baseConfig = buildBaseChartConfig(
        resolved.chartType as 'line' | 'area',
        resolved.palette,
        DEFAULT_FONT_FAMILY,
        DEFAULT_FORE_COLOR
    );
    const baseStates = buildChartStates();
    const baseXAxis = buildBaseXAxisConfig(XAXIS_LABEL_COLOR);
    const baseYAxis = buildBaseYAxisConfig((val: number) => resolved.formatValue(val), AREA_YAXIS_COLOR);

    // Resolve CSS variable to hex for ApexCharts gradient fill compatibility
    // ApexCharts doesn't support CSS variables in gradient colors array, so we resolve at runtime
    const gradientColor = resolveCssVariableToHex('var(--color-brand-main)');

    return {
        ...baseConfig,
        chart: {
            ...baseConfig.chart,
            stacked,
            sparkline: { enabled: false },
            animations: { enabled: false },
            zoom: { enabled: false },
        },
        stroke: stroke ?? { width: 2 },
        fill: {
            opacity: 1,
            type: 'gradient',
            gradient: {
                shade: 'light',
                type: 'vertical',
                shadeIntensity: 0.2,
                inverseColors: false,
                opacityFrom: 0.7,
                opacityTo: 0,
                stops: [0, 100],
                colorStops: [],
            },
            colors: [gradientColor],
        },
        grid: { show: false },
        legend: { show: false },
        plotOptions: { bar: { borderRadius: stacked ? 0 : 5 } },
        states: baseStates,
        xaxis: {
            ...baseXAxis,
            type: categories ? 'category' : 'datetime',
            categories,
            tickAmount: categories ? undefined : 8,
            tickPlacement: 'off',
            labels: {
                ...baseXAxis.labels,
                rotate: categories ? -45 : 0,
                trim: false,
                formatter: (v: string) => (categories ? String(v ?? '') : formatDateLabel(v)),
            },
            group: { style: { fontSize: '10px', fontWeight: 700 } },
        },
        yaxis: {
            ...baseYAxis,
            opposite: true,
            max: yAxisMax,
            min: yAxisMin,
            labels: {
                ...baseYAxis.labels,
                show: true,
                minWidth: 50,
            },
        },
        responsive: [...RESPONSIVE_BREAKPOINTS],
    };
}
